import React from 'react';
import {
  VictoryChart,
  VictoryBar,
  VictoryLine,
  VictoryAxis,
  VictoryTheme,
  VictoryLegend,
  VictoryScatter,
  VictoryLabel,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import { formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 09: Riesgo de Crédito
 *
 * Combined bar and line chart:
 * - Bars: Provisiones/Cartera Improductiva (coverage ratio) - left axis - teal
 * - Line: Cartera Improductiva/Cartera Bruta (delinquency rate) - right axis - dark gray
 *
 * Matches PDF report format from Atuntaqui_mar2025
 */

export interface Grafico09V2Data {
  period: string;              // YYYY-MM format
  cobertura_provisiones: number; // Provisiones/Improductiva (e.g., 0.9267 = 92.67%)
  mora: number;                // Cartera Improductiva/Cartera Bruta (e.g., 0.063 = 6.30%)
}

export interface Grafico09V2Props {
  data: Grafico09V2Data[];
  title?: string;
}

export const Grafico09V2: React.FC<Grafico09V2Props> = ({
  data,
  title = 'Gráfico 09: Riesgo de Crédito'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Bar data (Coverage ratio as percentage) - clean, no labels
  const barData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.cobertura_provisiones * 100,
  }));

  // Line data (Delinquency rate as percentage) - NO labels to avoid duplicates
  const lineData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.mora * 100,
  }));

  // Scatter data has labels for display
  const scatterData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.mora * 100,
    label: `${(d.mora * 100).toFixed(1)}%`
  }));

  // Calculate domains
  const maxCoverage = Math.max(...barData.map(d => d.y));
  const maxMora = Math.max(...lineData.map(d => d.y));

  // Left axis domain (Coverage: 0% to ~200%)
  const leftAxisMax = Math.ceil((maxCoverage * 1.15) / 50) * 50; // Round to nearest 50

  // Right axis domain (Mora: 0% to ~7%)
  const rightAxisMax = Math.ceil((maxMora * 1.5) / 1) * 1; // Round to nearest 1%

  // Scale factor to map mora values to coverage axis
  const scaleFactor = leftAxisMax / rightAxisMax;

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper chart-dual-axis">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 30, y: 10 }}
          padding={{ top: 50, bottom: 60, left: 55, right: 55 }}
          height={300}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) => datum.label}
              labelComponent={<VictoryTooltip style={{ fontSize: 9 }} />}
            />
          }
        >
          {/* Legend */}
          <VictoryLegend
            x={40}
            y={5}
            orientation="horizontal"
            gutter={20}
            style={{ labels: { fontSize: 8 } }}
            data={[
              { name: 'Provisiones/Improductiva', symbol: { fill: COLORS.secondary } },
              { name: 'Cartera Improductiva/Cartera Bruta', symbol: { fill: COLORS.primary, type: 'minus' } },
            ]}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              }
            }}
          />

          {/* Y-Axis Left (Coverage %) - 0% to 200% */}
          <VictoryAxis
            dependentAxis
            domain={[0, leftAxisMax]}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Y-Axis Right (Mora %) - 0% to 7% */}
          <VictoryAxis
            dependentAxis
            orientation="right"
            domain={[0, leftAxisMax]}
            tickValues={[0, leftAxisMax * 0.25, leftAxisMax * 0.5, leftAxisMax * 0.75, leftAxisMax]}
            tickFormat={(t: number) => `${(t / scaleFactor).toFixed(1)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 }
            }}
          />

          {/* Coverage Bars - teal */}
          <VictoryBar
            data={barData}
            barWidth={12}
            style={{ data: { fill: COLORS.secondary } }}
          />

          {/* Mora Line - scaled to coverage axis - dark gray */}
          <VictoryLine
            data={lineData.map(d => ({
              x: d.x,
              y: d.y * scaleFactor
            }))}
            style={{
              data: {
                stroke: COLORS.primary,
                strokeWidth: 1.2
              }
            }}
          />

          {/* Mora Points with small value labels - dark gray */}
          <VictoryScatter
            data={scatterData.map(d => ({
              ...d,
              y: d.y * scaleFactor
            }))}
            size={3}
            style={{ data: { fill: COLORS.primary } }}
            labelComponent={<VictoryLabel dy={-8} style={{ fontSize: 8, fill: COLORS.primary }} />}
          />
        </VictoryChart>
      </div>

      <p className="chart-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Grafico09V2;
